import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, UserCircle, Sparkles, Globe, Rocket, Users, TrendingUp, Layers, PlusCircle, Store, Clock, Eye, ExternalLink, Loader, Link2 } from 'lucide-react';
import TaskAI from '../components/TaskAI';
import { useAuth } from '../contexts/AuthContext';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import { TaskerProfile, Task } from '../types';
import { supabase, TABLES } from '../api/db';
import { TASK_UNIVERSES } from '../constants/taskCreation';

interface FeedTask extends Task {
  universe?: string;
  goal?: string;
  tools?: { id: string; name: string; category: string }[];
  source: 'marketplace' | 'hatch';
  connectionCount: number;
}

interface CreatorStats {
  totalTasks: number;
  universeBreakdown: { universe: string; emoji: string; color: string; label: string; pct: number }[];
}

const Home = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TaskerProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Feed state
  const [feedTasks, setFeedTasks] = useState<FeedTask[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [creatorStats, setCreatorStats] = useState<Record<string, CreatorStats>>({});

  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        try {
          setLoadingProfile(true);
          const data = await getTaskerProfileByUserId(currentUser.uid);
          setProfile(data);
        } catch (error) {
          console.error('Error loading profile:', error);
        } finally {
          setLoadingProfile(false);
        }
      }
    };
    loadProfile();
  }, [currentUser]);

  // Load public tasks feed with connection counts
  useEffect(() => {
    const loadFeed = async () => {
      setLoadingFeed(true);
      try {
        const [{ data: marketRows }, { data: hatchRows }, { data: connRows }] = await Promise.all([
          supabase.from(TABLES.TASKS).select('*').eq('visibility', 'public').order('created_at', { ascending: false }).limit(40),
          supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('visibility', 'public').order('created_at', { ascending: false }).limit(40),
          supabase.from(TABLES.CONNECTION_REQUESTS).select('task_id').eq('status', 'accepted'),
        ]);

        // Count connections per task
        const connCounts = new Map<string, number>();
        (connRows ?? []).forEach((r) => {
          const taskId = (r as { task_id: string }).task_id;
          connCounts.set(taskId, (connCounts.get(taskId) || 0) + 1);
        });

        const map = new Map<string, FeedTask>();
        (marketRows ?? []).forEach((r) => {
          const row = r as Record<string, unknown>;
          map.set(row['id'] as string, { ...(row as object), source: 'marketplace' as const, connectionCount: connCounts.get(row['id'] as string) || 0 } as FeedTask);
        });
        (hatchRows ?? []).forEach((r) => {
          const row = r as Record<string, unknown>;
          const id = row['id'] as string;
          if (!map.has(id)) {
            map.set(id, { ...(row as object), source: 'hatch' as const, connectionCount: connCounts.get(id) || 0 } as FeedTask);
          } else {
            const existing = map.get(id)!;
            existing.connectionCount = Math.max(existing.connectionCount, connCounts.get(id) || 0);
          }
        });

        // Sort: most connections first, then by recency
        const merged = Array.from(map.values()).sort((a, b) => {
          if (b.connectionCount !== a.connectionCount) return b.connectionCount - a.connectionCount;
          const aMs = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bMs = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bMs - aMs;
        });
        setFeedTasks(merged);

        // Build creator stats
        const byCreator = new Map<string, FeedTask[]>();
        merged.forEach((t) => {
          const list = byCreator.get(t.userId) || [];
          list.push(t);
          byCreator.set(t.userId, list);
        });

        const stats: Record<string, CreatorStats> = {};
        byCreator.forEach((tasks, uid) => {
          const universeCounts: Record<string, number> = {};
          tasks.forEach((t) => {
            const u = (t as any).universe || t.category || 'other';
            universeCounts[u] = (universeCounts[u] || 0) + 1;
          });
          const total = tasks.length;
          const breakdown = Object.entries(universeCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 4)
            .map(([uKey, count]) => {
              const entry = TASK_UNIVERSES.find((u) => u.id === uKey);
              return {
                universe: uKey,
                emoji: entry?.emoji || '📋',
                color: entry?.color || '#64748b',
                label: entry?.label || uKey,
                pct: Math.round((count / total) * 100),
              };
            });
          stats[uid] = { totalTasks: total, universeBreakdown: breakdown };
        });
        setCreatorStats(stats);
      } catch (err) {
        console.error('Error loading feed:', err);
      } finally {
        setLoadingFeed(false);
      }
    };
    loadFeed();
  }, []);

  const formatRelativeTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'Recently';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Recently';
    const now = Date.now();
    const ms = now - date.getTime();
    const mins = Math.floor(ms / 60000);
    const hrs = Math.floor(ms / 3600000);
    const days = Math.floor(ms / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const completionPercentage = profile?.completionPercentage || 0;

  // ── Guest landing ──────────────────────────────────────────────
  if (!currentUser) {
    return (
      <div className="min-h-full bg-tech-surface text-tech-text relative overflow-hidden bg-tech-mesh">
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 md:py-24">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-tech-cyan/20 bg-tech-cyan/5 text-xs font-medium text-tech-cyan mb-6">
              <Globe className="w-3.5 h-3.5" /> Global Network for Knowledge
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="text-tech-text">Build. Create. </span>
              <span className="bg-gradient-to-r from-tech-cyan to-tech-accent bg-clip-text text-transparent">Share.</span>
            </h1>
            <p className="text-lg sm:text-xl text-tech-text-muted max-w-2xl mx-auto mb-8 leading-relaxed">
              The platform for people who create with AI and technology. Showcase your tools, ship tasks,
              connect with collaborators, and build a profile worth sharing.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-tech-dark text-base transition-all hover:opacity-90 hover:shadow-[0_0_30px_rgba(0,102,255,0.3)]"
                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
              >
                Get Started <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-medium border border-tech-border text-tech-text hover:border-tech-cyan/40 hover:text-tech-cyan transition-all"
              >
                <Store className="w-4 h-4" /> Browse Tasks
              </Link>
            </div>
          </div>

          {/* Value props */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {[
              { icon: Rocket, title: 'Create Tasks', desc: 'Share what you\'re building with AI-powered tools and get collaborators', color: '#22d3ee' },
              { icon: Users, title: 'Connect', desc: 'Find people who share your skills and interests across the globe', color: '#34d399' },
              { icon: TrendingUp, title: 'Build Profile', desc: 'Every task you create and complete builds your shareable public profile', color: '#a855f7' },
              { icon: Globe, title: 'Share Knowledge', desc: 'Be part of a global network of creators, builders, and innovators', color: '#f59e0b' },
            ].map((item, i) => (
              <div key={i} className="tech-card rounded-2xl p-6 hover:border-tech-cyan/20 transition-all group">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: item.color + '15', border: `1px solid ${item.color}30` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <h3 className="text-base font-bold text-tech-text mb-1.5">{item.title}</h3>
                <p className="text-sm text-tech-text-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Trending public feed for guests */}
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-tech-cyan/10 to-tech-accent/10 border border-tech-cyan/20">
                  <TrendingUp className="w-5 h-5 text-tech-cyan" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-tech-text">Trending right now</h2>
                  <p className="text-xs text-tech-text-muted">The most connected tasks from the global network</p>
                </div>
              </div>
            </div>

            {loadingFeed ? (
              <div className="text-center py-16">
                <Loader className="w-7 h-7 animate-spin text-tech-cyan mx-auto mb-3" />
                <p className="text-sm text-tech-text-muted">Loading...</p>
              </div>
            ) : feedTasks.length === 0 ? (
              <div className="text-center py-12 tech-card rounded-2xl">
                <p className="text-tech-text-muted">No public tasks yet — sign up and be the first!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {feedTasks.slice(0, 6).map((task) => {
                  const taskUniverse = TASK_UNIVERSES.find((u) => u.id === task.universe);
                  const stats = creatorStats[task.userId];
                  const conns = task.connectionCount;

                  return (
                    <div
                      key={task.id}
                      className="group flex flex-col rounded-2xl border border-tech-border/40 bg-tech-gray/30 p-5 hover:border-tech-cyan/30 hover:bg-tech-gray/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.06)]"
                    >
                      {/* Universe + connections */}
                      <div className="flex items-center justify-between mb-3">
                        {taskUniverse ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border"
                            style={{ borderColor: taskUniverse.color + '40', background: taskUniverse.color + '12', color: taskUniverse.color }}
                          >
                            {taskUniverse.emoji} {taskUniverse.label}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-md bg-tech-gray text-[10px] text-tech-text-muted border border-tech-border/50">
                            {task.category || 'Task'}
                          </span>
                        )}
                        {conns > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-tech-accent">
                            <Link2 className="w-3 h-3" /> {conns} connected
                          </span>
                        ) : (
                          <span className="text-[10px] text-tech-text-muted flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatRelativeTime(task.createdAt)}
                          </span>
                        )}
                      </div>

                      <h3 className="text-sm font-bold text-tech-text mb-1 line-clamp-2 leading-snug group-hover:text-tech-cyan transition-colors">
                        {task.title}
                      </h3>
                      {task.taskCode && (
                        <span className="inline-flex items-center gap-1 mb-1 font-mono text-[10px] font-bold tracking-wider text-tech-cyan/60">
                          <span className="w-2.5 h-2.5 rounded-full bg-tech-cyan/20 flex items-center justify-center text-[7px]">#</span>
                          {task.taskCode}
                        </span>
                      )}
                      <p className="text-xs text-tech-text-muted line-clamp-2 mb-3">
                        {task.goal || task.summary}
                      </p>

                      <div className="flex-1" />

                      {/* Creator */}
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border"
                          style={{
                            background: (taskUniverse?.color || '#22d3ee') + '15',
                            borderColor: (taskUniverse?.color || '#22d3ee') + '30',
                            color: taskUniverse?.color || '#22d3ee',
                          }}
                        >
                          {(task.userName || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-tech-text truncate block">{task.userName || 'Anonymous'}</span>
                          {stats && (
                            <span className="text-[9px] text-tech-text-muted">{stats.totalTasks} task{stats.totalTasks !== 1 ? 's' : ''} created</span>
                          )}
                        </div>
                      </div>

                      {stats && stats.universeBreakdown.length > 0 && (
                        <div className="mb-3">
                          <div className="flex h-1 rounded-full overflow-hidden bg-tech-gray/50">
                            {stats.universeBreakdown.map((seg, i) => (
                              <div key={i} className="h-full" style={{ width: `${seg.pct}%`, background: seg.color }} />
                            ))}
                          </div>
                        </div>
                      )}

                      <Link
                        to="/signup"
                        className="w-full px-3 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 border border-tech-cyan/25 bg-tech-cyan/5 text-tech-cyan hover:bg-tech-cyan/15"
                      >
                        <Eye className="w-3.5 h-3.5" /> Sign up to connect
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <div className="inline-flex flex-col items-center gap-3 tech-card rounded-2xl px-8 py-6">
              <p className="text-sm text-tech-text-muted">For ages 8 to 66 and beyond</p>
              <p className="text-xs text-tech-text-muted/60">Anyone who creates with technology belongs here</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Authenticated dashboard ────────────────────────────────────
  return (
    <div className="min-h-full bg-tech-surface text-tech-text relative overflow-hidden bg-tech-mesh">
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Welcome header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-tech-text mb-1">
            Welcome back{currentUser.displayName ? `, ${currentUser.displayName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-tech-text-muted">
            Global Network for Knowledge — your workspace for building, creating, and connecting
          </p>
        </div>

        {/* Quick actions grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => navigate('/task-hatch')}
            className="group tech-card rounded-2xl p-5 text-left hover:border-tech-cyan/30 transition-all hover:shadow-[0_0_20px_rgba(34,211,238,0.08)]"
          >
            <div className="w-10 h-10 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <PlusCircle className="w-5 h-5 text-tech-cyan" />
            </div>
            <h3 className="text-sm font-bold text-tech-text mb-0.5">Create Task</h3>
            <p className="text-xs text-tech-text-muted">Start building something new</p>
          </button>

          <button
            onClick={() => navigate('/marketplace')}
            className="group tech-card rounded-2xl p-5 text-left hover:border-tech-accent/30 transition-all hover:shadow-[0_0_20px_rgba(52,211,153,0.08)]"
          >
            <div className="w-10 h-10 rounded-xl bg-tech-accent/10 border border-tech-accent/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Store className="w-5 h-5 text-tech-accent" />
            </div>
            <h3 className="text-sm font-bold text-tech-text mb-0.5">Marketplace</h3>
            <p className="text-xs text-tech-text-muted">Discover and connect on tasks</p>
          </button>

          <button
            onClick={() => navigate('/task-dashboard')}
            className="group tech-card rounded-2xl p-5 text-left hover:border-blue-400/30 transition-all hover:shadow-[0_0_20px_rgba(59,130,246,0.08)]"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Layers className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-sm font-bold text-tech-text mb-0.5">Task Hub</h3>
            <p className="text-xs text-tech-text-muted">Manage your tasks & connections</p>
          </button>

          <button
            onClick={() => navigate('/task-buddies')}
            className="group tech-card rounded-2xl p-5 text-left hover:border-purple-400/30 transition-all hover:shadow-[0_0_20px_rgba(168,85,247,0.08)]"
          >
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-sm font-bold text-tech-text mb-0.5">TaskBuddies</h3>
            <p className="text-xs text-tech-text-muted">Chat with your connections</p>
          </button>
        </div>

        {/* Profile status card */}
        {!loadingProfile && (
          <button
            onClick={() => navigate('/tasker-meta')}
            className="w-full tech-card rounded-2xl p-5 flex items-center gap-4 hover:border-tech-cyan/20 transition-all mb-6 text-left group"
          >
            <div className="relative shrink-0">
              <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(0,217,255,0.15)" strokeWidth="3" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="url(#homeGrad)" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={2 * Math.PI * 28 * (1 - completionPercentage / 100)}
                  className="transition-all duration-1000" />
                <defs><linearGradient id="homeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00d9ff" /><stop offset="100%" stopColor="#00ff88" />
                </linearGradient></defs>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-tech-cyan">{completionPercentage}%</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <UserCircle className="w-4 h-4 text-tech-cyan" />
                <span className="text-sm font-bold text-tech-text">
                  {profile ? 'Your Profile' : 'Create Your Profile'}
                </span>
              </div>
              <p className="text-xs text-tech-text-muted">
                {profile
                  ? completionPercentage < 100
                    ? `${100 - completionPercentage}% remaining to complete — a complete profile gets you matched with tasks`
                    : 'Profile complete! Share it to get discovered by collaborators'
                  : 'Build your profile to showcase your skills and get matched with tasks'}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-tech-cyan opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </button>
        )}

        {/* ── Trending Tasks Feed ────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-tech-cyan/10 to-tech-accent/10 border border-tech-cyan/20">
                <TrendingUp className="w-5 h-5 text-tech-cyan" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-tech-text">Trending Tasks</h2>
                <p className="text-xs text-tech-text-muted">Most connected tasks from the global network</p>
              </div>
            </div>
            <Link
              to="/marketplace"
              className="text-xs font-medium text-tech-cyan hover:text-tech-text transition-colors flex items-center gap-1"
            >
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loadingFeed ? (
            <div className="text-center py-16">
              <Loader className="w-7 h-7 animate-spin text-tech-cyan mx-auto mb-3" />
              <p className="text-sm text-tech-text-muted">Loading trending tasks...</p>
            </div>
          ) : feedTasks.length === 0 ? (
            <div className="text-center py-12 tech-card rounded-2xl">
              <TrendingUp className="w-12 h-12 text-tech-text-muted/30 mx-auto mb-3" />
              <p className="text-tech-text font-medium mb-1">No public tasks yet</p>
              <p className="text-sm text-tech-text-muted mb-4">Be the first to create and share a task!</p>
              <button
                onClick={() => navigate('/task-hatch')}
                className="px-5 py-2.5 rounded-xl font-semibold text-tech-dark text-sm"
                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
              >
                Create a Task
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {feedTasks.slice(0, 15).map((task, rank) => {
                const taskUniverse = TASK_UNIVERSES.find((u) => u.id === task.universe);
                const stats = creatorStats[task.userId];
                const tools = (task.tools || []) as { name: string }[];
                const conns = task.connectionCount;

                return (
                  <div
                    key={task.id}
                    className="group relative rounded-2xl border border-tech-border/50 bg-gradient-to-br from-tech-gray/40 to-tech-dark/30 hover:border-tech-cyan/25 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.05)] overflow-hidden"
                  >
                    {/* Universe color accent */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                      style={{ background: taskUniverse ? `linear-gradient(to bottom, ${taskUniverse.color}, ${taskUniverse.color}60)` : 'linear-gradient(to bottom, #22d3ee, #3b82f6)' }}
                    />

                    <div className="p-5 pl-5">
                      {/* Creator row */}
                      <div className="flex items-center justify-between mb-3">
                        <Link
                          to={`/tasker/${task.userId}`}
                          className="flex items-center gap-3 min-w-0 group/creator"
                        >
                          <div className="relative">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 border"
                              style={{
                                background: (taskUniverse?.color || '#22d3ee') + '15',
                                borderColor: (taskUniverse?.color || '#22d3ee') + '30',
                                color: taskUniverse?.color || '#22d3ee',
                              }}
                            >
                              {(task.userName || '?').charAt(0).toUpperCase()}
                            </div>
                            {/* Rank badge for top 3 */}
                            {rank < 3 && conns > 0 && (
                              <span className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full text-[9px] font-black flex items-center justify-center"
                                style={{ background: ['#FFD700', '#C0C0C0', '#CD7F32'][rank], color: '#0a0a0f' }}>
                                {rank + 1}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-semibold text-tech-text group-hover/creator:text-tech-cyan transition-colors truncate block">
                              {task.userName || 'Anonymous'}
                            </span>
                            <div className="flex items-center gap-2 text-[10px] text-tech-text-muted">
                              {stats && (
                                <span className="flex items-center gap-1">
                                  <Layers className="w-2.5 h-2.5" />
                                  {stats.totalTasks} task{stats.totalTasks !== 1 ? 's' : ''}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                {formatRelativeTime(task.createdAt)}
                              </span>
                            </div>
                          </div>
                        </Link>

                        <div className="flex items-center gap-2 shrink-0">
                          {/* Connection count badge */}
                          {conns > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-tech-accent/30 bg-tech-accent/10 text-tech-accent">
                              <Link2 className="w-3 h-3" /> {conns} connected
                            </span>
                          )}
                          {taskUniverse && (
                            <span
                              className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border"
                              style={{ borderColor: taskUniverse.color + '40', background: taskUniverse.color + '12', color: taskUniverse.color }}
                            >
                              {taskUniverse.emoji} {taskUniverse.label}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Task content */}
                      <div className="mb-3">
                        <h3 className="text-base font-bold text-tech-text mb-1 group-hover:text-tech-cyan transition-colors leading-snug">
                          {task.title}
                        </h3>
                        {task.taskCode && (
                          <span className="inline-flex items-center gap-1 mb-1 font-mono text-[10px] font-bold tracking-wider text-tech-cyan/60">
                            <span className="w-2.5 h-2.5 rounded-full bg-tech-cyan/20 flex items-center justify-center text-[7px]">#</span>
                            {task.taskCode}
                          </span>
                        )}
                        <p className="text-sm text-tech-text-muted line-clamp-2 leading-relaxed">
                          {task.goal || task.summary}
                        </p>
                      </div>

                      {/* Tools + tags */}
                      {(tools.length > 0 || (task.tags || []).length > 0) && (
                        <div className="flex flex-wrap items-center gap-1.5 mb-3">
                          {tools.slice(0, 3).map((t, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-md bg-tech-gray/80 text-[10px] text-tech-text-muted border border-tech-border/50">
                              {t.name}
                            </span>
                          ))}
                          {tools.length > 3 && (
                            <span className="text-[10px] text-tech-text-muted">+{tools.length - 3}</span>
                          )}
                          {(task.tags || []).slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="text-[10px] text-tech-cyan/60">#{tag}</span>
                          ))}
                        </div>
                      )}

                      {/* Creator universe breakdown bar */}
                      {stats && stats.universeBreakdown.length > 0 && (
                        <div className="mb-3">
                          <div className="flex h-1.5 rounded-full overflow-hidden bg-tech-gray/50">
                            {stats.universeBreakdown.map((seg, i) => (
                              <div
                                key={i}
                                className="h-full transition-all duration-500"
                                style={{ width: `${seg.pct}%`, background: seg.color }}
                                title={`${seg.label}: ${seg.pct}%`}
                              />
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                            {stats.universeBreakdown.map((seg, i) => (
                              <span key={i} className="text-[9px] text-tech-text-muted flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: seg.color }} />
                                {seg.emoji} {seg.label} {seg.pct}%
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/tasks/${task.id}`)}
                          className="flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 border border-tech-cyan/25 bg-tech-cyan/5 text-tech-cyan hover:bg-tech-cyan/15"
                        >
                          <Eye className="w-3.5 h-3.5" /> View Task
                        </button>
                        <Link
                          to={`/tasker/${task.userId}`}
                          className="px-3 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 border border-tech-border bg-tech-gray/30 text-tech-text-muted hover:border-tech-cyan/30 hover:text-tech-cyan"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Profile
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* View more */}
              <div className="text-center pt-2">
                <Link
                  to="/marketplace"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl border border-tech-border bg-tech-gray/30 text-sm font-medium text-tech-text-muted hover:border-tech-cyan/30 hover:text-tech-cyan transition-all"
                >
                  Explore all tasks in Marketplace <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Global Network for Knowledge banner */}
        <div className="tech-card rounded-2xl p-6 border-tech-cyan/10 bg-gradient-to-r from-tech-cyan/5 via-transparent to-tech-accent/5">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-tech-cyan" />
            <h2 className="text-lg font-bold text-tech-text">Global Network for Knowledge</h2>
          </div>
          <p className="text-sm text-tech-text-muted leading-relaxed max-w-3xl">
            GNK Continuum connects creators, builders, and innovators across the world. Every task you create,
            every connection you make, and every project you ship builds your presence in the global knowledge network.
            Share your public profile, discover collaborators, and be part of something bigger.
          </p>
        </div>
      </div>

      {currentUser && <TaskAI />}
    </div>
  );
};

export default Home;
